import React, { useState, useCallback } from 'react';
import {
    DndContext,
    DragOverlay,
    DragStartEvent,
    DragEndEvent,
    DragOverEvent,
    ClientRect,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faSitemap,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import ms from 'milsymbol';
import parse from 'html-react-parser';
import {
    Faction,
    UnitGroupTreeResponse,
    UnitTreeNode,
    UnitCreate,
    UnitUpdate,
    UnitGroupCreate,
    InsertionIndicator,
} from '../../types/orbat';
import UnitNode from './UnitNode';
import { UnitModal, UnitSubmitData, EditableUnit } from '../unit/UnitModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface OrbatCanvasProps {
    faction: Faction | null;
    factionAffiliationName?: string; // e.g. "FRIENDLY", "HOSTILE"
    unitGroupTree: UnitGroupTreeResponse | null;
    hasUnitGroups: boolean;
    isLoading: boolean;
    error: string | null;
    onCreateUnitGroup: (data: UnitGroupCreate) => Promise<void>;
    onCreateUnit: (data: UnitCreate) => Promise<void>;
    onUpdateUnit: (unitId: string, data: UnitUpdate) => Promise<void>;
    onUpdateUnitPosition: (
        unitId: string,
        newParentId: string | null,
        newOrder: number
    ) => Promise<void>;
    onReorderUnits: (orderedUnitIds: string[]) => Promise<void>;
    onDeleteUnit: (unitId: string) => Promise<void>;
}

/**
 * Decide whether the dragged unit lands before or after the hovered sibling by
 * comparing the two rects along the sibling group's layout axis. Level-2
 * siblings are laid out horizontally (compare X); deeper levels stack
 * vertically (compare Y). Falls back to "after" when rects are unavailable.
 */
function resolveEdge(
    draggedRect: ClientRect | null | undefined,
    targetRect: ClientRect | null | undefined,
    horizontal: boolean
): 'before' | 'after' {
    if (!draggedRect || !targetRect) return 'after';
    const draggedCenter = horizontal
        ? draggedRect.left + draggedRect.width / 2
        : draggedRect.top + draggedRect.height / 2;
    const targetCenter = horizontal
        ? targetRect.left + targetRect.width / 2
        : targetRect.top + targetRect.height / 2;
    return draggedCenter < targetCenter ? 'before' : 'after';
}

/**
 * Canvas component displaying the ORBAT tree visualization with drag-and-drop.
 */
const OrbatCanvas: React.FC<OrbatCanvasProps> = ({
    faction,
    factionAffiliationName,
    unitGroupTree,
    hasUnitGroups,
    isLoading,
    error,
    onCreateUnitGroup,
    onCreateUnit,
    onUpdateUnit,
    onUpdateUnitPosition,
    onReorderUnits,
    onDeleteUnit,
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [parentUnitForAdd, setParentUnitForAdd] = useState<UnitTreeNode | null>(null);
    const [editingUnit, setEditingUnit] = useState<UnitTreeNode | null>(null);
    const [deletingUnit, setDeletingUnit] = useState<UnitTreeNode | null>(null);
    const [activeNode, setActiveNode] = useState<UnitTreeNode | null>(null);
    // While dragging over a sibling, where the dragged unit would be inserted.
    const [insertion, setInsertion] = useState<InsertionIndicator | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    // Configure drag sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum drag distance before activation
            },
        })
    );

    // Check if targetId is a descendant of node
    const isDescendant = useCallback((node: UnitTreeNode, targetId: string): boolean => {
        if (node.id === targetId) return true;
        for (const child of node.children || []) {
            if (isDescendant(child, targetId)) return true;
        }
        return false;
    }, []);

    // Find the parent of a node in the tree
    const findParentNode = useCallback((tree: UnitTreeNode | null, targetId: string): UnitTreeNode | null => {
        if (!tree) return null;

        // Check if target is a direct child of current node
        if (tree.children?.some(child => child.id === targetId)) {
            return tree;
        }

        // Recursively search in children
        for (const child of tree.children || []) {
            const parent = findParentNode(child, targetId);
            if (parent) return parent;
        }

        return null;
    }, []);

    // Handle drag start
    const handleDragStart = useCallback((event: DragStartEvent) => {
        const { active } = event;
        const node = active.data.current?.node as UnitTreeNode;
        setActiveNode(node);
    }, []);

    // While dragging, show an insertion line when hovering over a sibling
    // (same parent) — that gesture reorders rather than reparents.
    const handleDragOver = useCallback(
        (event: DragOverEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) {
                setInsertion(null);
                return;
            }
            const draggedNode = active.data.current?.node as UnitTreeNode;
            const overNode = over.data.current?.node as UnitTreeNode;
            const tree = unitGroupTree?.tree ?? null;
            if (!draggedNode || !overNode || !tree) {
                setInsertion(null);
                return;
            }
            const draggedParent = findParentNode(tree, draggedNode.id);
            const overParent = findParentNode(tree, overNode.id);
            const areSiblings =
                !!draggedParent && !!overParent && draggedParent.id === overParent.id;
            if (!areSiblings) {
                setInsertion(null);
                return;
            }
            const horizontal = draggedParent!.id === tree.id; // level-2 row
            const edge = resolveEdge(
                active.rect.current.translated,
                over.rect,
                horizontal
            );
            setInsertion({ nodeId: overNode.id, edge });
        },
        [unitGroupTree, findParentNode]
    );

    const handleDragCancel = useCallback(() => {
        setActiveNode(null);
        setInsertion(null);
    }, []);

    // Handle drag end
    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            setActiveNode(null);
            setInsertion(null);

            if (!over || active.id === over.id) {
                return;
            }

            const draggedNode = active.data.current?.node as UnitTreeNode;
            const dropTarget = over.data.current?.node as UnitTreeNode;

            if (!draggedNode || !dropTarget) {
                return;
            }

            const tree = unitGroupTree?.tree ?? null;
            const draggedParent = findParentNode(tree, draggedNode.id);
            const dropParent = findParentNode(tree, dropTarget.id);
            const areSiblings =
                !!draggedParent &&
                !!dropParent &&
                draggedParent.id === dropParent.id;

            try {
                if (areSiblings && tree) {
                    // Same parent → reorder within the sibling group.
                    const parent = draggedParent!;
                    const horizontal = parent.id === tree.id; // level-2 row
                    const edge = resolveEdge(
                        active.rect.current.translated,
                        over.rect,
                        horizontal
                    );
                    const siblings = [...(parent.children ?? [])].sort(
                        (a, b) => a.unit_order - b.unit_order
                    );
                    const without = siblings.filter((s) => s.id !== draggedNode.id);
                    const targetIdx = without.findIndex((s) => s.id === dropTarget.id);
                    if (targetIdx === -1) return;
                    const insertIdx = edge === 'before' ? targetIdx : targetIdx + 1;
                    const reordered = [
                        ...without.slice(0, insertIdx),
                        draggedNode,
                        ...without.slice(insertIdx),
                    ];
                    const orderedIds = reordered.map((n) => n.id);
                    // Skip a no-op reorder (dropped back into its own slot).
                    if (orderedIds.join(',') === siblings.map((n) => n.id).join(',')) {
                        return;
                    }
                    await onReorderUnits(orderedIds);
                } else {
                    // Different parent → reparent as the new parent's last child.
                    // Prevent dropping a node onto itself or its descendants.
                    if (isDescendant(draggedNode, dropTarget.id)) {
                        return;
                    }
                    const newOrder = dropTarget.children?.length || 0;
                    await onUpdateUnitPosition(draggedNode.id, dropTarget.id, newOrder);
                }
            } catch (err) {
                console.error('Failed to update unit position:', err);
            }
        },
        [isDescendant, onUpdateUnitPosition, onReorderUnits, unitGroupTree, findParentNode]
    );

    // Handle adding a new unit
    const handleAddUnit = (parentUnit: UnitTreeNode | null) => {
        setParentUnitForAdd(parentUnit);
        setEditingUnit(null); // Ensure we're in create mode
        setModalError(null);
        setShowAddModal(true);
    };

    // Handle editing a unit (double-click)
    const handleEditUnit = (unit: UnitTreeNode) => {
        setEditingUnit(unit);
        // Find parent unit to enable echelon filtering in edit mode
        const parent = unitGroupTree?.tree ? findParentNode(unitGroupTree.tree, unit.id) : null;
        setParentUnitForAdd(parent);
        setModalError(null);
        setShowAddModal(true);
    };

    // Handle creating a new ORBAT (unit group with root unit)
    const handleCreateOrbat = async () => {
        if (!faction) return;

        setModalLoading(true);
        setModalError(null);

        try {
            await onCreateUnitGroup({
                name: 'Main ORBAT',
                faction_id: faction.id,
            });
        } catch (err) {
            setModalError(err instanceof Error ? err.message : 'Failed to create ORBAT');
        } finally {
            setModalLoading(false);
        }
    };

    // Handle unit modal submission (create or edit)
    const handleUnitSubmit = async (data: UnitSubmitData) => {
        if (!unitGroupTree || !faction) return;

        setModalLoading(true);
        setModalError(null);

        try {
            // Strip the dynamic `dtg` overlay before persisting — the server
            // derives it from position_time at render time, so it is not a
            // real text modifier. Mirrors the marker edit path.
            const { dtg: _dtg, ...textModifiers } =
                (data.markerProperties ?? {}) as Record<string, unknown>;

            if (editingUnit && data.id) {
                // Edit mode
                await onUpdateUnit(data.id, {
                    name: data.name,
                    sidc: data.sidc,
                    is_publicly_joinable: data.isPubliclyJoinable,
                    text_modifiers: textModifiers,
                });
                setEditingUnit(null);
            } else {
                // Create mode
                await onCreateUnit({
                    name: data.name || '',
                    sidc: data.sidc,
                    faction_id: faction.id,
                    unit_group_id: unitGroupTree.unit_group.id,
                    upper_unit_id: data.upper_unit_id,
                    is_publicly_joinable: data.isPubliclyJoinable,
                    text_modifiers: textModifiers,
                });
                setParentUnitForAdd(null);
            }
            setShowAddModal(false);
        } catch (err) {
            setModalError(err instanceof Error ? err.message : 'Failed to save unit');
            throw err;
        } finally {
            setModalLoading(false);
        }
    };

    // Handle delete click
    const handleDeleteClick = (unit: UnitTreeNode) => {
        setDeletingUnit(unit);
    };

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (!deletingUnit) return;

        setModalLoading(true);
        try {
            await onDeleteUnit(deletingUnit.id);
            setDeletingUnit(null);
        } catch (err) {
            console.error('Failed to delete unit:', err);
        } finally {
            setModalLoading(false);
        }
    };

    // Adapt the snake_case tree node into the modal's camelCase EditableUnit shape.
    // Keeps the API <-> UI boundary explicit so the modal doesn't need to know
    // about the wire format. `is_publicly_joinable` is optional on the wire
    // (older backend deploys may omit it), so coerce missing values to `false`
    // here at the read boundary.
    const editingUnitForModal: EditableUnit | null = editingUnit
        ? {
              id: editingUnit.id,
              name: editingUnit.name,
              sidc: editingUnit.sidc,
              isPubliclyJoinable: editingUnit.is_publicly_joinable ?? false,
              markerProperties: editingUnit.text_modifiers,
          }
        : null;

    // Generate drag overlay symbol
    const dragOverlaySymbol = activeNode
        ? parse(new ms.Symbol(activeNode.sidc, { size: 40 }).asSVG())
        : null;

    // Empty state - no faction selected
    if (!faction) {
        return (
            <div className="orbat-canvas">
                <div className="orbat-canvas__empty">
                    <FontAwesomeIcon icon={faSitemap} />
                    <p>Select a faction to view its ORBAT</p>
                </div>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="orbat-canvas">
                <div className="orbat-canvas__header">
                    <h5>{faction.name}</h5>
                </div>
                <div className="orbat-canvas__content">
                    <div className="orbat-loading">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="orbat-canvas">
                <div className="orbat-canvas__header">
                    <h5>{faction.name}</h5>
                </div>
                <div className="orbat-canvas__content">
                    <Alert variant="danger" className="m-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {error}
                    </Alert>
                </div>
            </div>
        );
    }

    // Check if unit group exists and has a tree
    const hasTree = unitGroupTree !== null && unitGroupTree.tree != null;

    // No unit groups yet - show "Create ORBAT" button
    if (!hasUnitGroups) {
        return (
            <div className="orbat-canvas">
                <div className="orbat-canvas__header">
                    <h5>{faction.name}</h5>
                </div>
                <div className="orbat-canvas__content">
                    <div className="orbat-canvas__empty">
                        <FontAwesomeIcon icon={faSitemap} />
                        <p>No ORBAT created for this faction yet.</p>
                        <Button
                            variant="primary"
                            onClick={handleCreateOrbat}
                            disabled={modalLoading}
                        >
                            {modalLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        className="me-2"
                                    />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Create ORBAT
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="orbat-canvas">
            <div className="orbat-canvas__header">
                <h5>{faction.name}</h5>
            </div>
            <div className="orbat-canvas__content">
                {!hasTree ? (
                    <div className="orbat-canvas__empty">
                        <FontAwesomeIcon icon={faSitemap} />
                        <p>Loading ORBAT...</p>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                    >
                        <div className="unit-tree">
                            <div className="unit-tree__root">
                                <UnitNode
                                    node={unitGroupTree!.tree!}
                                    eventId={unitGroupTree!.unit_group.event_id}
                                    isRoot
                                    insertion={insertion}
                                    onAddChild={handleAddUnit}
                                    onDelete={handleDeleteClick}
                                    onEdit={handleEditUnit}
                                />
                            </div>
                        </div>

                        {/* Drag Overlay */}
                        <DragOverlay>
                            {activeNode && (
                                <div className="drag-overlay">
                                    <div className="unit-node__card">
                                        <div className="unit-node__symbol">
                                            {dragOverlaySymbol}
                                        </div>
                                        <div className="unit-node__name">
                                            {activeNode.name}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </DragOverlay>
                    </DndContext>
                )}
            </div>

            {/* Add/Edit Unit Modal */}
            <UnitModal
                show={showAddModal}
                onHide={() => {
                    setShowAddModal(false);
                    setParentUnitForAdd(null);
                    setEditingUnit(null);
                    setModalError(null);
                }}
                context="orbat"
                mode={editingUnit ? 'edit' : 'create'}
                editingUnit={editingUnitForModal}
                parentUnit={parentUnitForAdd}
                onSubmit={handleUnitSubmit}
                factionAffiliationName={factionAffiliationName}
                lockIdentity={true}
                showTextModifiers={true}
                isLoading={modalLoading}
                error={modalError}
                // ORBAT creator UX enhancements
                hideIdentity={true}
                filterEchelonByParent={true}
                simplifyHqtfdToDummy={true}
                simplifyStatusToPlanned={true}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                show={!!deletingUnit}
                onHide={() => setDeletingUnit(null)}
                onConfirm={handleDeleteConfirm}
                title="Delete Unit"
                message={`Are you sure you want to delete "${deletingUnit?.name}"? This will also delete all subordinate units.`}
                isLoading={modalLoading}
            />
        </div>
    );
};

export default OrbatCanvas;
